import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { Alert } from 'shared/ui'
import { Button } from 'shared/ui/Button'
import { EnumIconButtonShape, IconButton } from 'shared/ui/IconButton'
import { useTextareaAutoResize } from 'shared/hooks'
import { ChatbotConversationStatus } from 'entities/Chatbot/model/type'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import styles from './styles.module.scss'

export const TestAgentFooter = observer(() => {
  const { testAgentStore, autoFollowUpSectionStore } = useUnifiedAgentContext()
  const { loading, inputValue, setInputValue, completed, isExistChanges, started, status } =
    testAgentStore
  const textareaRef = useTextareaAutoResize(inputValue, 20, 252)

  const handleSend = async () => {
    await testAgentStore.sendMessage()
    textareaRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const isSendButton = inputValue.trim() && !loading
  const showSaveAndRestart = started && isExistChanges && !completed
  const isSendDisabled = loading || completed || showSaveAndRestart

  const completedDesc =
    status === ChatbotConversationStatus.Completed
      ? 'Success actions will be triggered for real contacts.'
      : 'Fallback actions will be triggered for real contacts.'

  return (
    <div className={styles.footer}>
      {completed && (
        <Alert
          item={{
            type: 'success',
            title: 'Agent test completed',
            desc: completedDesc,
            hideIcon: true,
          }}
          actions={[
            {
              variant: 'button',
              type: 'primary',
              text: 'Restart agent',
              onClick: testAgentStore.restart,
              disabled: loading,
            },
          ]}
        />
      )}
      {showSaveAndRestart && (
        <Alert
          item={{
            type: 'warning',
            title: 'Changes detected',
            desc: 'To test the latest changes, save and restart the session.',
            hideIcon: true,
          }}
          actions={[
            {
              variant: 'button',
              type: 'primary',
              text: 'Save & Restart',
              onClick: testAgentStore.saveAndRestart,
              disabled: loading,
            },
          ]}
        />
      )}
      {!completed && !showSaveAndRestart && (
        <div className={styles.footerActions}>
          <Button
            typeBtn='text'
            text='Restart agent'
            size='small'
            onClick={testAgentStore.restart}
            disabled={loading}
            color={'var(--content-brand-primary)'}
          />
          {autoFollowUpSectionStore.enabled && (
            <Button
              typeBtn='text'
              color={'var(--content-brand-primary)'}
              text='Test follow-up'
              size='small'
              onClick={testAgentStore.testFollowup}
              disabled={loading}
            />
          )}
        </div>
      )}
      <div
        className={classnames(styles.inputRow, {
          [styles.centered]: !isSendButton,
          [styles.disabled]: loading || completed,
        })}
      >
        <textarea
          ref={textareaRef}
          className={styles.input}
          placeholder='Write a message...'
          value={inputValue}
          rows={1}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSendDisabled}
        />
        <IconButton
          fontSize={16}
          icon='send'
          color={isSendButton ? 'primary' : 'neutral'}
          variant={isSendButton ? 'contained' : 'icon'}
          size={isSendButton ? 'medium' : 'small'}
          disabled={!inputValue.trim() || isSendDisabled}
          ariaLabel='TestAgentSend'
          onClick={handleSend}
          className={styles.sendBtn}
          shape={EnumIconButtonShape.Rounded}
        />
      </div>
    </div>
  )
})
